import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { updatePermission, useOrgPermissions } from "@/services/role.services";
import Checkbox from "@/common/custom/Chekbox";
import CustomRenderSimpleTableView from "@/common/Table/CustomRenderSimpleTableView";
import { RoundLoader } from "@/common/ui/Loader";
import CustomButton from "@/common/ui/Button";
import { toastMessage } from "@/utils/helper/myToast";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import ConfirmationAlertModal from "@/components/dashboard/setting/security/modal/ConfirmationAlertModal";
import { requestUserConfirmation } from "@/utils/helper/promise";
import { loginSelector } from "@/store/features/loginSlice";
import { useSelector } from "react-redux";
import { isHasPermission } from "@/utils/helper/permissions";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";

const DisableMfaAlertModalContent = {
  descriptions: ["Are you sure you want to save changes?"],
  submitText: "Save",
  submitBtnVariant: "primary",
};

// Types
type PermissionRoleValue = {
  checked: boolean;
  isDisabled: boolean;
};

type PermissionRole = {
  roleId: string;
  roleName: string;
  values: PermissionRoleValue;
};

type Permission = {
  id: number;
  description?: string;
  roles: PermissionRole[];
};

type PermissionModule = {
  moduleId: number;
  moduleName: string;
  permissions: Permission[];
};

const moduleMotion = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};
const modalConfig: ModalConfig = {
  userAlertConfirmation: {
    component: ConfirmationAlertModal,
    title: "Save changes",
    modalClassName: "!max-w-[330px]",
  },
};
const ManagePermissionComponent: React.FC<any> = ({ openModal, closeModal }) => {
  const { data, isLoading, refetch } = useOrgPermissions();
  const { currentUser } = useSelector(loginSelector);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const hasSetOpenModules = useRef(false);
  const [checkedState, setCheckedState] = useState<Record<number, Record<string, Record<string, boolean>>>>({});
  const [changedState, setChangedState] = useState<Record<number, Record<string, Record<string, boolean>>>>({});

  const permissionModules: PermissionModule[] = Array.isArray(data) ? data : data?.data ?? [];

  const [openModuleIds, setOpenModuleIds] = useState<number[]>(() => {
    if (permissionModules.length > 0) {
      return [permissionModules[0].moduleId];
    }
    return [];
  });

  useEffect(() => {
    if (!hasSetOpenModules.current && permissionModules.length > 0) {
      setOpenModuleIds([permissionModules[0].moduleId]);
      hasSetOpenModules.current = true;
    }
  }, [permissionModules]);

  const toggleModule = (moduleId: number) => {
    setOpenModuleIds((prev) => (prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]));
  };

  const handleEdit = () => {
    const newCheckedState: Record<number, Record<string, Record<string, boolean>>> = {};
    permissionModules.forEach((module) => {
      const permissionChecked: Record<string, Record<string, boolean>> = {};
      module.permissions.forEach((perm) => {
        permissionChecked[perm.id] = {};
        perm.roles.forEach((role) => {
          permissionChecked[perm.id][role.roleId] = role.values.checked;
        });
      });
      newCheckedState[module.moduleId] = permissionChecked;
    });
    setCheckedState(newCheckedState);
    setChangedState({});
    setIsEditMode(true);
  };

  const handleSave = async () => {
    await requestUserConfirmation({ openModal, closeModal, modalKey: "userAlertConfirmation", modalProps: DisableMfaAlertModalContent });
    closeModal();
    // Prepare changed data only
    const changedPayload: any[] = [];
    Object.entries(changedState).forEach(([moduleId, permsObj]) => {
      // roleId -> perms[]
      const roleMap: Record<string, { permId: string; checked: boolean }[]> = {};
      Object.entries(permsObj).forEach(([permId, rolesObj]) => {
        Object.entries(rolesObj).forEach(([roleId, checked]) => {
          if (!roleMap[roleId]) roleMap[roleId] = [];
          roleMap[roleId].push({ permId, checked });
        });
      });
      const roles = Object.entries(roleMap).map(([roleId, perms]) => ({ roleId, perms }));
      changedPayload.push({ moduleId: Number(moduleId), roles });
    });
    if (changedPayload.length === 0) return;
    try {
      setIsBtnLoading(true);
      // console.log(changedPayload);
      const res: any = await updatePermission(changedPayload);
      if (res?.status === 200) {
        setIsEditMode(false);
        const { message, isError } = res.data;
        refetch();
        return toastMessage(message, isError);
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
      setIsEditMode(false);
      setChangedState({});
    } catch (err) {
      // handle error (show toast, etc)
      console.error(err);
    } finally {
      setIsBtnLoading(false);
    }
  };

  const handleCheckboxChange = (moduleId: number, permissionId: number, roleId: string, checked: boolean) => {
    setCheckedState((prev) => ({
      ...prev,
      [moduleId]: {
        ...(prev[moduleId] || {}),
        [permissionId]: {
          ...(prev[moduleId]?.[permissionId] || {}),
          [roleId]: checked,
        },
      },
    }));
    // Track changes only if value differs from initial
    const initial = permissionModules
      .find((m) => m.moduleId === moduleId)
      ?.permissions.find((p) => p.id === permissionId)
      ?.roles.find((r) => r.roleId === roleId)?.values.checked;
    if (checked !== initial) {
      setChangedState((prev) => ({
        ...prev,
        [moduleId]: {
          ...(prev[moduleId] || {}),
          [permissionId]: {
            ...(prev[moduleId]?.[permissionId] || {}),
            [roleId]: checked,
          },
        },
      }));
    } else {
      setChangedState((prev) => {
        const newState = { ...prev };
        if (newState[moduleId]?.[permissionId]) {
          delete newState[moduleId][permissionId][roleId];
        }
        return newState;
      });
    }
  };

  const isSaveDisabled = useMemo(() => Object.keys(changedState).length === 0, [changedState]);

  if (!isHasPermission(currentUser?.perms, PERMISSIONS.ROLES["Manage Permission"])) {
    return <PermissionDenied />;
  }
  return (
    <div className="h-[calc(100%-4px)] relative overflow-y-auto">
      <div className="flex justify-between items-center pb-4 sticky top-0 z-30 bg-white p-4">
        <div className="flex items-center">
          <h2 className="text-13 md:text-lg font-semibold text-text-default">Manage permissions</h2>
          {/* <Button variant="ghost" size="sm" onClick={() => refetch()} className="p-2 hover:bg-gray-100 rounded-full">
            <Icon src="/icons/refresh.svg" alt="refresh icon" className={`size-5 ${isLoading || isFetching ? "animate-spin-reverse" : ""}`} />
          </Button> */}
        </div>

        <div className="flex gap-2">
          {isEditMode ? (
            <div className="flex justify-end w-full gap-2">
              <CustomButton variant="outline" size="small" onClick={() => setIsEditMode(false)} disabled={isBtnLoading}>
                Cancel
              </CustomButton>
              <CustomButton variant="primary" size="small" onClick={handleSave} disabled={isSaveDisabled} isLoading={isBtnLoading}>
                Save changes
              </CustomButton>
            </div>
          ) : (
            <CustomButton
              variant="outline"
              size="small"
              className="!text-13 !text-text-primary !bg-primaryOpacity-10 !border-none"
              isLoading={false}
              onClick={handleEdit}
            >
              Edit permissions
            </CustomButton>
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-6 items-center flex justify-center h-[calc(100%-114px)]">
          <RoundLoader />
        </div>
      ) : (
        <div className="space-y-6 md:px-4 pb-4 sticky top-0">
          {permissionModules.map((module) => {
            // Dynamically extract all roles for this module
            const allRoles = module.permissions?.[0]?.roles?.map((r) => ({ roleId: r.roleId, roleName: r.roleName })) ?? [];
            return (
              <motion.div
                key={module.moduleId}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={moduleMotion}
                className="rounded-xl border border-gray-200 shadow-sm bg-white"
              >
                <div
                  className="flex items-center gap-3 px-4 py-4 cursor-pointer select-none  hover:bg-gray-50 rounded-t-xl"
                  onClick={() => toggleModule(module.moduleId)}
                >
                  <Button variant="ghost" size="sm" className="rounded-s p-2 border">
                    {openModuleIds.includes(module.moduleId) ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                  </Button>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-13 text-text-default">{module.moduleName}</span>
                    {/* <span className="text-xs text-text-light2">Manage permissions for user actions across different roles.</span> */}
                  </div>
                </div>
                <AnimatePresence>
                  {openModuleIds.includes(module.moduleId) && (
                    <motion.div
                      key="content"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-x-auto max-h-[400px] relative"
                    >
                      <CustomRenderSimpleTableView
                        permissions={Array.isArray(module.permissions) ? module.permissions : []}
                        roles={allRoles}
                        isEditMode={isEditMode}
                        checkedState={checkedState[module.moduleId]}
                        handleCheckboxChange={(permId: number, roleId: string, checked: boolean) =>
                          handleCheckboxChange(module.moduleId, permId, roleId, checked)
                        }
                        renderCell={({ checked, isDisabled, onChange, isEditMode }) => (
                          <Checkbox
                            checked={checked}
                            className="!p-1"
                            checkBoxSize="w-[15px] h-[15px]"
                            onChange={onChange}
                            disabled={isEditMode ? isDisabled : true}
                            disabledClassName="opacity-80"
                            checkClassName={isEditMode ? "text-white" : undefined}
                            checkBoxClassName={isEditMode ? "peer-checked:bg-text-primary" : undefined}
                            checkSize=""
                          />
                        )}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ManagePermission = withModal(ManagePermissionComponent, modalConfig);
export default ManagePermission;
